/**
 * src/components/GuardedCheckout.js
 *
 * ONE CHECKOUT CONTROLLER FOR EVERY PURCHASE SURFACE.
 *
 * The backend refuses a checkout with `409 immediate_access_consent_required` when the customer's
 * jurisdiction requires an express acknowledgement before immediate supply. Showing that refusal as
 * an ordinary red error is not a finished experience: the customer is being told "no" without being
 * given the thing that would make it "yes".
 *
 * This turns the refusal into an acknowledgement step, once, for every surface:
 *
 *     purchase action
 *       -> attempt checkout
 *       -> 409 with the server's own wording
 *       -> render the shared acknowledgement
 *       -> customer explicitly acknowledges
 *       -> record consent server-side
 *       -> retry the SAME checkout
 *       -> Stripe
 *
 * Design rules:
 *
 *   - NO LEGAL RULES HERE. This file contains no country, no regime, no withdrawal period and no
 *     wording. Every sentence shown comes from the 409 payload, which the resolver built. It cannot
 *     tell a service from digital content and does not try to - it prints what it is given.
 *
 *   - NOTHING CHANGES FOR CUSTOMERS WHO DO NOT NEED IT. If the checkout succeeds, nothing renders
 *     and nothing is asked. A customer in Australia sees exactly what they saw before.
 *
 *   - THE SERVER STAYS AUTHORITATIVE. If this wiring regresses, the backend gate still refuses the
 *     charge. This improves the experience; it is not the control.
 *
 *   - NEVER PRE-TICKED. The acknowledgement starts unchecked, every time it is shown.
 */
import React, { useCallback, useState } from 'react';
import API from '../api';

export const CONSENT_REQUIRED_ERROR = 'immediate_access_consent_required';

/**
 * True when an axios error is the consent refusal rather than a real failure.
 */
export function isConsentRequired(error) {
  const response = error && error.response;
  return !!(response && response.status === 409 &&
            response.data && response.data.error === CONSENT_REQUIRED_ERROR);
}

/**
 * The checkout controller.
 *
 * `start(runCheckout, { purchaseType, intendedSelector })` runs the caller's own checkout request.
 * `runCheckout` must return the axios promise for the real checkout call, so this controller can
 * retry the exact same request after the acknowledgement.
 */
export function useGuardedCheckout() {
  const [pending, setPending] = useState(null);   // { runCheckout, purchaseType, intendedSelector }
  const [prompt, setPrompt] = useState(null);     // the server's wording, from the 409
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setPending(null);
    setPrompt(null);
    setPolicy(null);
    setError('');
  }, []);

  const start = useCallback(async (runCheckout, options) => {
    const opts = options || {};
    setBusy(true);
    setError('');
    try {
      const res = await runCheckout();
      reset();
      return res;
    } catch (err) {
      if (isConsentRequired(err)) {
        // Not a failure: a step the customer has not taken yet.
        const data = err.response.data;
        setPending({
          runCheckout,
          purchaseType: opts.purchaseType || null,
          intendedSelector: opts.intendedSelector || null,
        });
        setPrompt(data.immediate_access_consent_prompt || null);
        setPolicy(data.policy || null);
        setError('');
        return null;
      }
      setError((err.response && err.response.data && (err.response.data.message
                                                      || err.response.data.error))
               || 'Something went wrong starting this purchase. Please try again.');
      return null;
    } finally {
      setBusy(false);
    }
  }, [reset]);

  /**
   * Record the acknowledgement server-side, then retry the original checkout.
   * The browser sends only its two explicit confirmations; every legal fact is the server's.
   */
  const acknowledgeAndRetry = useCallback(async () => {
    if (!pending) return null;
    setBusy(true);
    setError('');
    try {
      await API.post('/v1/commerce-policy/consent', {
        purchase_type: pending.purchaseType,
        immediate_access_consent: true,
        acknowledge_loss_of_cancellation_right: true,
        intended_selector: pending.intendedSelector,
      });
      const res = await pending.runCheckout();
      reset();
      return res;
    } catch (err) {
      setError((err.response && err.response.data && (err.response.data.message
                                                      || err.response.data.error))
               || 'The purchase could not be started. Please try again.');
      return null;
    } finally {
      setBusy(false);
    }
  }, [pending, reset]);

  return {
    start,
    acknowledgeAndRetry,
    cancel: reset,
    consentRequired: !!pending,
    prompt,
    policy,
    busy,
    error,
  };
}

/**
 * The acknowledgement block. Renders nothing unless the server asked for one.
 *
 * Drop `<GuardedCheckoutConsent checkout={checkout} />` into any purchase surface next to its
 * buttons; it is inert on every purchase that does not need it.
 */
export default function GuardedCheckoutConsent({ checkout, onDone }) {
  // NEVER pre-ticked. This is the only initialiser and it is always false.
  const [ticked, setTicked] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  if (!checkout || !checkout.consentRequired || !checkout.prompt) return null;

  const copy = (checkout.policy && checkout.policy.customer_copy) || {};

  const confirm = async () => {
    const result = await checkout.acknowledgeAndRetry();
    if (result && onDone) onDone(result);
  };

  return (
    <div style={gc.block} data-testid="guarded-consent-block">
      <div style={gc.heading}>One thing before we start</div>

      <label style={gc.row}>
        <input
          type="checkbox"
          checked={ticked}
          onChange={(e) => setTicked(e.target.checked)}
          style={gc.checkbox}
          data-testid="guarded-consent-checkbox"
        />
        <span style={gc.label}>{checkout.prompt.label}</span>
      </label>

      <p style={gc.explanation}>{checkout.prompt.explanation}</p>

      <button type="button" style={gc.linkBtn} onClick={() => setShowTerms(!showTerms)}>
        {showTerms ? 'Hide refund and cancellation terms' : 'Refund and cancellation terms'}
      </button>

      {showTerms ? (
        <div style={gc.terms} data-testid="guarded-consent-terms">
          {copy.voluntary ? <p style={gc.termsText}>{copy.voluntary}</p> : null}
          {copy.statutory ? <p style={gc.termsText}>{copy.statutory}</p> : null}
          {copy.statutory_remedies ? <p style={gc.termsText}>{copy.statutory_remedies}</p> : null}
        </div>
      ) : null}

      {checkout.error ? <div style={gc.err}>{checkout.error}</div> : null}

      <div style={gc.actions}>
        <button
          type="button"
          style={{ ...gc.confirmBtn, opacity: (!ticked || checkout.busy) ? 0.5 : 1 }}
          disabled={!ticked || checkout.busy}
          onClick={confirm}
          data-testid="guarded-consent-confirm"
        >
          {checkout.busy ? 'Working...' : 'Confirm and continue'}
        </button>
        <button type="button" style={gc.cancelBtn} onClick={checkout.cancel}>
          Not now
        </button>
      </div>
    </div>
  );
}

const gc = {
  block: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(120,173,255,0.35)',
    borderRadius: 12,
    padding: '18px 20px',
    margin: '18px 0',
  },
  heading: { fontSize: 13, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
             color: '#78ADFF', marginBottom: 12 },
  row: { display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' },
  checkbox: { marginTop: 3, width: 18, height: 18, flexShrink: 0, cursor: 'pointer' },
  label: { fontSize: 14, lineHeight: 1.55, color: '#e8eefc', fontWeight: 600 },
  explanation: { margin: '10px 0 0 30px', fontSize: 12.5, lineHeight: 1.6, color: '#9ca3af' },
  linkBtn: {
    background: 'none', border: 'none', padding: '10px 0 0 30px', cursor: 'pointer',
    color: '#78ADFF', fontSize: 12.5, fontWeight: 700, textDecoration: 'underline',
  },
  terms: {
    marginTop: 10, marginLeft: 30, padding: '12px 14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
  },
  termsText: { margin: '0 0 8px', fontSize: 12.5, lineHeight: 1.6, color: '#cbd5e1' },
  err: { marginTop: 12, marginLeft: 30, fontSize: 13, color: '#fca5a5' },
  actions: { display: 'flex', gap: 12, marginTop: 16, marginLeft: 30 },
  confirmBtn: {
    background: '#2F7DFF', color: '#fff', border: 'none', borderRadius: 999,
    padding: '10px 22px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
  cancelBtn: {
    background: 'transparent', color: '#9ca3af',
    border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999,
    padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
};
